import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faStop } from "@fortawesome/free-solid-svg-icons";
import { IdeOutputPanel } from "../python-runtime/IdeOutputPanel";
import { usePythonRuntime } from "../python-runtime/usePythonRuntime";
import { LessonCodeEditor } from "./lesson-workspace/LessonCodeEditor";
import { PresentationDeck, type PresentationDeckSlide } from "./PresentationDeck";
import { PresentationFitStage } from "./PresentationFitStage";
import {
  PRESENTATION_FIT_DESIGN_WIDTH,
  PRESENTATION_FIT_DESIGN_WIDTH_LIVE,
} from "./fitPresentationScale";

export type PresentationLiveCodeMap = Record<string, string>;

export function liveCodeForSlide(
  slide: PresentationDeckSlide,
  drafts: PresentationLiveCodeMap
): string {
  if (Object.prototype.hasOwnProperty.call(drafts, slide.id)) return drafts[slide.id] ?? "";
  return slide.code ?? "";
}

export function PresentationPresentView(props: {
  readonly title: string;
  readonly slide: PresentationDeckSlide;
  readonly index: number;
  readonly total: number;
  readonly liveCode: string;
  readonly onLiveCodeChange: (code: string) => void;
  readonly onResetCode: () => void;
  readonly chromeVisible: boolean;
  readonly onMouseMove: () => void;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onExit?: () => void;
  readonly onDownloadPdf?: () => void;
  readonly presentRootRef: RefObject<HTMLDivElement | null>;
  readonly variant?: "overlay" | "embedded";
  readonly requestFullscreen?: boolean;
  readonly autoHideChrome?: boolean;
  readonly leadingAction?: { readonly label: string; readonly onClick: () => void };
  readonly exitLabel?: string;
  readonly nextLabel?: string;
  readonly nextDisabled?: boolean;
  readonly consoleHint?: string;
}) {
  const {
    title,
    slide,
    index,
    total,
    liveCode,
    onLiveCodeChange,
    onResetCode,
    chromeVisible,
    onMouseMove,
    onPrev,
    onNext,
    onExit,
    onDownloadPdf,
    presentRootRef,
    variant = "overlay",
    requestFullscreen,
    autoHideChrome,
    leadingAction,
    exitLabel = "Exit",
    nextLabel = "Next",
    nextDisabled,
    consoleHint = "Edits stay only while you present. Ctrl+Enter (or Cmd+Enter) runs.",
  } = props;
  const overlay = variant === "overlay";
  const doFullscreen = requestFullscreen ?? overlay;
  const hideChrome = (autoHideChrome ?? overlay) && !chromeVisible;
  const disableNext = nextDisabled ?? index >= total - 1;
  const runtime = usePythonRuntime();
  const {
    phase,
    runtimeError,
    lines,
    inputPrompt,
    canUseSharedMemory,
    turtleHtml,
    turtleTabVisible,
    outputTab,
    setOutputTab,
    run,
    stop,
    submitInput,
    clearConsole,
  } = runtime;
  const [inputDraft, setInputDraft] = useState("");
  const [clearOnRun, setClearOnRun] = useState(true);
  const consoleInputRef = useRef<HTMLInputElement | null>(null);
  const showLive = typeof slide.code === "string";
  const running = phase === "running" || phase === "awaiting_input";
  const loadingRuntime = phase === "loading";
  const originalCode = slide.code ?? "";
  const codeDirty = liveCode !== originalCode;

  useLayoutEffect(() => {
    if (!doFullscreen) return;
    const el = presentRootRef.current;
    if (!el) return;
    const anyEl = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
    const fn = el.requestFullscreen?.bind(el) ?? anyEl.webkitRequestFullscreen?.bind(el);
    if (fn) void Promise.resolve(fn()).catch(() => undefined);
  }, [doFullscreen, presentRootRef]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [slide.id, stop]);

  const onRun = useCallback(() => {
    if (running || loadingRuntime || !canUseSharedMemory) return;
    if (clearOnRun) clearConsole();
    run(liveCode, "slide.py");
  }, [canUseSharedMemory, clearConsole, clearOnRun, liveCode, loadingRuntime, run, running]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        onRun();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onRun]);

  const onSubmitInput = useCallback(() => {
    submitInput(inputDraft);
    setInputDraft("");
  }, [inputDraft, submitInput]);

  return (
    <div
      ref={presentRootRef}
      className={`presentation-present${showLive ? " presentation-present--live" : ""}${overlay ? "" : " presentation-present--embedded"}`}
      onMouseMove={onMouseMove}
      role={overlay ? "dialog" : undefined}
      aria-label={`${title} presentation`}
    >
      <div className={`presentation-present-stage${showLive ? " presentation-present-stage--split" : ""}`}>
        <div className="presentation-present-main">
          <PresentationFitStage
            designWidth={showLive ? PRESENTATION_FIT_DESIGN_WIDTH_LIVE : PRESENTATION_FIT_DESIGN_WIDTH}
            contentKey={slide.id}
          >
            <PresentationDeck
              slide={slide}
              index={index}
              total={total}
              showCount={false}
              hideCode={showLive}
              className="presentation-deck-slide--present"
            />
          </PresentationFitStage>
          {showLive ? (
            <div className="presentation-live-editor-wrap">
              <p className="presentation-live-editor-label">Code (not saved)</p>
              <LessonCodeEditor
                key={slide.id}
                value={liveCode}
                onChange={onLiveCodeChange}
                minHeight="16rem"
                ariaLabel="Slide Python code"
              />
            </div>
          ) : null}
        </div>
        {showLive ? (
          <aside className="presentation-present-console" aria-label="Slide console">
            <div className="presentation-present-console-toolbar">
              <span className={`ide-status ide-status--${phase}`} aria-live="polite">
                {loadingRuntime
                  ? "Loading Python…"
                  : phase === "running"
                    ? "Running"
                    : phase === "awaiting_input"
                      ? "Waiting for input"
                      : runtimeError
                        ? "Unavailable"
                        : "Ready"}
              </span>
              <button
                type="button"
                className="btn secondary tight"
                onClick={() => clearConsole()}
                disabled={running}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn secondary tight"
                onClick={onResetCode}
                disabled={running || !codeDirty}
              >
                Reset code
              </button>
              <button
                type="button"
                className="btn secondary tight"
                onClick={() => stop()}
                disabled={!running || loadingRuntime}
              >
                <FontAwesomeIcon icon={faStop} /> Stop
              </button>
              <button
                type="button"
                className="btn primary tight"
                onClick={onRun}
                disabled={running || loadingRuntime || !canUseSharedMemory}
              >
                <FontAwesomeIcon icon={faPlay} /> Run
              </button>
            </div>
            {runtimeError ? (
              <p className="warn-banner" role="alert">
                {runtimeError}
              </p>
            ) : null}
            <IdeOutputPanel
              lines={lines}
              phase={phase}
              inputPrompt={inputPrompt}
              inputDraft={inputDraft}
              onInputDraftChange={setInputDraft}
              onSubmitInput={onSubmitInput}
              consoleInputRef={consoleInputRef}
              clearConsoleOnRun={clearOnRun}
              onClearConsoleOnRunChange={setClearOnRun}
              outputTab={outputTab}
              onOutputTabChange={setOutputTab}
              turtleHtml={turtleHtml}
              turtleTabVisible={turtleTabVisible}
            />
            <p className="muted small presentation-present-console-hint">{consoleHint}</p>
          </aside>
        ) : null}
      </div>
      {overlay ? (
        <div className={`presentation-present-bar${hideChrome ? " presentation-present-bar--hidden" : ""}`}>
          <button type="button" className="btn secondary" disabled={index <= 0} onClick={onPrev}>
            Previous
          </button>
          <p className="presentation-present-count">
            {index + 1} / {total}
          </p>
          <button type="button" className="btn primary" disabled={disableNext} onClick={onNext}>
            {nextLabel}
          </button>
          {onDownloadPdf ? (
            <button type="button" className="btn secondary" onClick={onDownloadPdf}>
              Download PDF
            </button>
          ) : null}
          {onExit ? (
            <button type="button" className="btn secondary" onClick={onExit}>
              {exitLabel}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="presentation-present-bar presentation-present-bar--page">
          {leadingAction ? (
            <button type="button" className="btn secondary" onClick={leadingAction.onClick}>
              {leadingAction.label}
            </button>
          ) : (
            <span />
          )}
          <div className="presentation-present-bar-nav">
            <button type="button" className="btn secondary" disabled={index <= 0} onClick={onPrev}>
              Previous
            </button>
            <p className="presentation-present-count">
              {index + 1} / {total}
            </p>
            <button type="button" className="btn primary" disabled={disableNext} onClick={onNext}>
              {nextLabel}
            </button>
          </div>
          {onExit && !leadingAction ? (
            <button type="button" className="btn secondary" onClick={onExit}>
              {exitLabel}
            </button>
          ) : (
            <span />
          )}
        </div>
      )}
    </div>
  );
}
